'use client';

import { Icon, IconType } from '../icons';
import { TMessageBarProps, cn, TMessageBarSeverity, TIconProps } from 'types';

const severityStyles: Record<TMessageBarSeverity, string> = {
  [TMessageBarSeverity.WARNING]: 'bg-oker20 border-1 border-oker',
  [TMessageBarSeverity.SUCCESS]: 'bg-komorebi20 border-1 border-komorebi',
  [TMessageBarSeverity.INFORMATION]: 'bg-blue20 border-1 border-blue',
  [TMessageBarSeverity.ERROR]: 'bg-lightDestructive border-1 border-poppy',
  [TMessageBarSeverity.NEUTRAL]: 'bg-concrete border-1 border-concrete',
};

const severityIconMap: Record<TMessageBarSeverity, { icon: IconType; className: string }> = {
  [TMessageBarSeverity.WARNING]: {
    icon: IconType.Warning,
    className: 'text-oker',
  },
  [TMessageBarSeverity.SUCCESS]: {
    icon: IconType.NewNextCheckFilled,
    className: 'text-komorebi',
  },
  [TMessageBarSeverity.INFORMATION]: {
    icon: IconType.CircleInfo,
    className: 'text-blue',
  },
  [TMessageBarSeverity.ERROR]: {
    icon: IconType.FilledCircleWarning,
    className: 'text-destructive',
  },
  [TMessageBarSeverity.NEUTRAL]: {
    icon: IconType.NewNextWarningSolid,
    className: 'text-oker',
  },
};

export function MessageBar({
  severity = TMessageBarSeverity.INFORMATION,
  children,
  className,
  dismissible = false,
  alertIcon,
  onClose,
  alertIconClassName,
}: TMessageBarProps) {
  const { icon, className: iconClass } = severityIconMap[severity];
  const iconObject = typeof alertIcon === 'object';
  const iconProps: TIconProps = iconObject
    ? {
        icon: alertIcon?.icon || icon,
        width: alertIcon?.size || 24,
        height: alertIcon?.size || 24,
        fill: alertIcon?.fill || 'currentColor',
      }
    : { icon: alertIcon || icon, width: 24, height: 24 };

  return (
    <div
      className={cn(
        'relative flex w-fit rounded-sm h-fit gap-2',
        severityStyles[severity],
        className?.includes('tw-small') ? 'px-3 py-2' : 'px-4 py-3 max-sm:py-2',
        className
      )}
    >
      {/* Icon */}
      <div className={cn('flex items-start mt-[3px]', iconClass, alertIconClassName)}>
        <Icon fill="currentColor" {...iconProps} />
      </div>

      {/* Content */}
      <div className="flex-1">{children}</div>

      {/* Close button */}
      {dismissible && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close message"
          className="tw-dismiss-button cursor-pointer relative flex items-center justify-center w-6 h-6 rounded-full transition-colors hover:bg-black/5 focus:outline-none"
        >
          <Icon icon={IconType.Close} width={24} height={24} className="text-black" />
        </button>
      )}
    </div>
  );
}
